import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface UseNavigationGuardProps {
  shouldBlock: boolean; // returned from your dirty-check logic
}

export const useNavigationGuard = ({ shouldBlock }: UseNavigationGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  const isNavigatingRef = useRef(false);
  const unblockRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    if (!shouldBlock) {
      // cleanup if needed
      unblockRef.current?.();
      unblockRef.current = null;
      return;
    }

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const blockNavigation = (url: string) => {
      if (isNavigatingRef.current) return true;

      const targetPath = new URL(url, window.location.origin).pathname;
      const currentPath = location.pathname;

      if (targetPath !== currentPath) {
        setPendingPath(targetPath);
        setIsModalOpen(true);
        return false;
      }

      return true;
    };

    window.history.pushState = function (state, title, url) {
      if (url && !blockNavigation(url.toString())) return;
      return originalPushState.call(this, state, title, url);
    };

    window.history.replaceState = function (state, title, url) {
      if (url && !blockNavigation(url.toString())) return;
      return originalReplaceState.call(this, state, title, url);
    };

    const handlePopState = (event: PopStateEvent) => {
      if (!isNavigatingRef.current && shouldBlock) {
        event.preventDefault();
        window.history.pushState(null, '', location.pathname); // revert
        setPendingPath(location.pathname);
        setIsModalOpen(true);
      }
    };

    window.addEventListener('popstate', handlePopState);

    unblockRef.current = () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handlePopState);
    };

    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
  }, [shouldBlock, location.pathname]);

  // User confirms navigation
  const confirmNavigation = () => {
    isNavigatingRef.current = true;

    if (pendingPath) navigate(pendingPath);

    setIsModalOpen(false);
    setPendingPath(null);
  };

  // User cancels navigation
  const cancelNavigation = () => {
    setIsModalOpen(false);
    setPendingPath(null);
  };

  return {
    isModalOpen,
    confirmNavigation,
    cancelNavigation,
  };
};
